/**
 * CSV Import — Contacts, Accounts, Opening Balances, Journals.
 * Two-step: preview (validate) → commit.
 */
import React, { useState } from 'react';
import { Upload, Download, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { booksClient, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const KINDS = [
  { path: 'contacts', label: 'Contacts (Customers + Vendors)', columns: ['type', 'name', 'display_name', 'email', 'phone', 'tax_registration', 'billing_address', 'shipping_address', 'default_tax_code', 'payment_terms_days'] },
  { path: 'accounts', label: 'Chart of Accounts', columns: ['code', 'name', 'type', 'subtype'] },
  { path: 'opening-balances', label: 'Opening Balances (single journal)', columns: ['account_code', 'debit', 'credit', 'description'], needsDate: true },
  { path: 'journals', label: 'Journals (grouped by ref_id)', columns: ['ref_id', 'date', 'narration', 'account_code', 'debit', 'credit', 'description', 'cost_center'] },
];

const CsvImportPage = () => {
  const [kind, setKind] = useState(KINDS[0]);
  const [file, setFile] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (commit) => {
    if (!file) return;
    setBusy(true); setPreview(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('commit', commit ? 'true' : 'false');
    if (kind.needsDate) fd.append('as_of_date', asOfDate);
    try {
      const r = await booksClient.post(`/csv-import/${kind.path}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      if (commit) toast({ title: `Imported ${r.data.committed || r.data.posted?.length || 0} records` });
    } catch (e) {
      toast({ title: errMsg(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const rows = [kind.columns.join(',')];
    const example = { type: 'customer', name: 'Acme Corp', account_code: '1000', code: '4020', debit: '50000',
      credit: '', description: 'Opening bank', ref_id: 'J1', date: '2026-02-15', narration: 'Rent Feb' };
    rows.push(kind.columns.map((c) => example[c] || '').join(','));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${kind.path}_template.csv`;
    a.click();
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Data Migration</div>
        <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>CSV Import</h1>
        <div className="text-sm text-[#0b1a2e]/60 mt-1">Upload → validate → commit. Nothing is written until you click <em>Commit Import</em>.</div>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <L label="Import kind">
            <select value={kind.path} onChange={(e) => { setKind(KINDS.find((k) => k.path === e.target.value)); setPreview(null); setFile(null); }}
              data-testid="csv-kind" className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
              {KINDS.map((k) => <option key={k.path} value={k.path}>{k.label}</option>)}
            </select>
          </L>
          <L label="CSV file">
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files[0])} data-testid="csv-file"
              className="w-full text-xs" />
          </L>
          <button onClick={downloadTemplate} data-testid="csv-template"
            className="inline-flex items-center gap-1 px-4 py-2 border border-[#0f5c3d] text-[#0f5c3d] rounded-full text-sm font-semibold">
            <Download className="w-3.5 h-3.5" /> Template
          </button>
        </div>
        {kind.needsDate && (
          <div className="mt-3">
            <L label="As-of date"><input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          </div>
        )}
        <div className="mt-4 text-xs text-[#0b1a2e]/50">Expected columns: <span className="font-mono">{kind.columns.join(', ')}</span></div>
        <div className="mt-4 flex gap-2">
          <button onClick={() => upload(false)} disabled={!file || busy} data-testid="csv-preview"
            className="inline-flex items-center gap-1 px-5 py-2 border border-[#0f5c3d] text-[#0f5c3d] rounded-full text-sm font-semibold disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Preview
          </button>
          <button onClick={() => upload(true)} disabled={!file || busy || !preview} data-testid="csv-commit"
            className="inline-flex items-center gap-1 px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-40">
            <CheckCircle2 className="w-3.5 h-3.5" /> Commit Import
          </button>
        </div>
      </div>

      {preview && <PreviewCard data={preview} kind={kind} />}
    </div>
  );
};

const PreviewCard = ({ data, kind }) => {
  const valid = data.valid ?? data.journals_ready ?? data.valid_lines ?? 0;
  const invalid = data.invalid ?? (data.journal_errors?.length || 0) + (data.row_errors?.length || 0) ?? 0;
  const errors = data.errors || data.row_errors || data.journal_errors || [];
  const committed = data.committed;

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-5">
      <div className="grid grid-cols-4 gap-3 mb-4">
        <MetaTile label="Total rows" value={data.total_rows} />
        <MetaTile label="Valid" value={valid} color="text-emerald-700" />
        <MetaTile label="Errors" value={invalid} color={invalid ? 'text-red-700' : 'text-[#0b1a2e]'} />
        <MetaTile label={committed && committed !== false ? 'Committed' : 'Preview only'} value={committed || (committed === false ? '—' : '')} color="text-[#0f5c3d]" />
      </div>

      {kind.path === 'opening-balances' && (
        <div className={`mb-4 p-3 rounded text-sm ${data.balanced ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {data.balanced ? '✓ Balanced' : '✗ Not balanced'} — D {data.total_debit} · C {data.total_credit}
        </div>
      )}

      {errors.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-red-700 mb-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Errors (first {Math.min(errors.length, 20)})
          </div>
          <div className="bg-red-50 rounded p-3 text-xs space-y-1 max-h-48 overflow-auto">
            {errors.slice(0, 20).map((e, i) => (
              <div key={i} className="font-mono">
                Line {e.line || e.ref_id}: {e.error}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.preview?.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-2">Preview (first 10)</div>
          <div className="bg-[#f7fbf8] rounded p-3 text-xs max-h-64 overflow-auto">
            <pre className="whitespace-pre-wrap font-mono">{JSON.stringify(data.preview.slice(0, 10), null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

const MetaTile = ({ label, value, color = 'text-[#0b1a2e]' }) => (
  <div className="text-center p-3 bg-[#f7fbf8] rounded-lg">
    <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">{label}</div>
    <div className={`mt-1 text-2xl font-mono font-semibold ${color}`}>{value}</div>
  </div>
);

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default CsvImportPage;
